import React, { useRef } from "react";
import { useContent } from "../context/ContentContext";
import { newSection, SECTION_LABELS } from "../mock";
import {
  ArrowUp,
  ArrowDown,
  Trash2,
  Plus,
  Palette,
  Image as ImageIcon,
  Upload,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";

export function resolveColor(value, theme) {
  if (!value) return "#ffffff";
  if (value === "var(--navy)") return theme.navy;
  if (value === "var(--red)") return theme.red;
  if (value === "var(--lightblue)") return theme.lightblue;
  return value;
}

function ColorControl({ label, value, onChange, theme }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <span className="text-sm text-slate-700">{label}</span>
      <input
        type="color"
        value={resolveColor(value, theme)}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 w-12 cursor-pointer rounded border border-slate-300 bg-white p-0.5"
      />
    </div>
  );
}

/** Floating controls shown on each section while editing. */
export function SectionControls({ section }) {
  const {
    content,
    moveSection,
    removeSection,
    updateSection,
    addSection,
  } = useContent();
  const theme = content.theme;
  const hasColors = section.type !== "hero" && section.type !== "footerlogo";

  return (
    <div className="absolute right-2 top-2 z-30 flex items-center gap-1 rounded-md border border-slate-200 bg-white/95 p-1 shadow-lg backdrop-blur">
      <button
        title="Move up"
        onClick={() => moveSection(section.id, -1)}
        className="rounded p-1.5 text-slate-600 hover:bg-slate-100"
      >
        <ArrowUp size={16} />
      </button>
      <button
        title="Move down"
        onClick={() => moveSection(section.id, 1)}
        className="rounded p-1.5 text-slate-600 hover:bg-slate-100"
      >
        <ArrowDown size={16} />
      </button>

      {hasColors && (
        <Popover>
          <PopoverTrigger asChild>
            <button
              title="Colors"
              className="rounded p-1.5 text-slate-600 hover:bg-slate-100"
            >
              <Palette size={16} />
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-56">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
              Section colors
            </p>
            <ColorControl
              label="Background"
              theme={theme}
              value={section.bg}
              onChange={(v) => updateSection(section.id, { bg: v })}
            />
            {"color" in section && (
              <ColorControl
                label="Text"
                theme={theme}
                value={section.color}
                onChange={(v) => updateSection(section.id, { color: v })}
              />
            )}
          </PopoverContent>
        </Popover>
      )}

      <button
        title="Delete section"
        onClick={() => {
          if (window.confirm("Delete this section?"))
            removeSection(section.id);
        }}
        className="rounded p-1.5 text-red-600 hover:bg-red-50"
      >
        <Trash2 size={16} />
      </button>

      <div className="mx-0.5 h-5 w-px bg-slate-200" />

      <AddSectionMenu afterId={section.id} onAdd={addSection} />
    </div>
  );
}

export function AddSectionMenu({ afterId, onAdd, label }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          title="Add section below"
          className="flex items-center gap-1 rounded bg-[var(--navy)] px-2 py-1.5 text-xs font-semibold text-white hover:opacity-90"
        >
          <Plus size={15} />
          {label || "Add"}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {Object.entries(SECTION_LABELS).map(([type, name]) => (
          <DropdownMenuItem
            key={type}
            onClick={() => onAdd(newSection(type), afterId)}
          >
            {name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Reads an image file and returns a downscaled data URL (max 1920px, JPEG). */
export function readImageFile(file) {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const scale = Math.min(1, 1920 / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL("image/jpeg", 0.82));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.readAsDataURL(file);
    };
    img.src = url;
  });
}

/** Editable image with URL + file upload options, shown while editing. */
export function ImageEditable({ src, alt, onChange, className, style }) {
  const { editMode, isAdmin } = useContent();
  const fileRef = useRef(null);
  const editing = editMode && isAdmin;

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    onChange(await readImageFile(file));
    e.target.value = "";
  };

  if (!editing) {
    return <img src={src} alt={alt} className={className} style={style} />;
  }

  return (
    <div className="group relative">
      <img src={src} alt={alt} className={className} style={style} />
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/45 opacity-0 transition-opacity group-hover:opacity-100">
        <button
          onClick={() => {
            const url = window.prompt("Image URL:", src);
            if (url) onChange(url);
          }}
          className="flex items-center gap-2 rounded bg-white px-3 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-100"
        >
          <ImageIcon size={16} /> Change URL
        </button>
        <button
          onClick={() => fileRef.current?.click()}
          className="flex items-center gap-2 rounded bg-white px-3 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-100"
        >
          <Upload size={16} /> Upload image
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFile}
        />
      </div>
    </div>
  );
}
